import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useState,
} from 'react';
import {
  SalaryStructure,
  SalaryStructureComponent,
  SalaryStructureComponentValueType,
} from '../types';
import { SalaryStructureContextType } from './types';

const SalaryStructureContext = createContext<SalaryStructureContextType | undefined>(undefined);

type Action =
  | { type: 'SET_SALARY_STRUCTURE_COMPONENTS'; data: SalaryStructureComponent[] }
  | { type: 'UPDATE_COMPONENT'; id: string; value: SalaryStructureComponent }
  | { type: 'UPDATE_AMOUNT'; id: string; value?: string }
  | { type: 'UPDATE_TYPE'; id: string; value: SalaryStructureComponentValueType }
  | { type: 'ADD_COMPONENT'; id: string; salaryComponent: SalaryStructureComponent }
  | { type: 'REMOVE_COMPONENT'; id: string };

function salaryStructureReducer(
  state: SalaryStructureComponent[],
  action: Action,
): SalaryStructureComponent[] {
  switch (action.type) {
    case 'UPDATE_COMPONENT':
      return state.map((item) => (item.id === action.id ? action.value : item));
    case 'UPDATE_AMOUNT':
      return state.map((item) =>
        item.id === action.id ? { ...item, formula: action.value || '' } : item,
      );
    case 'UPDATE_TYPE':
      return state.map((item) =>
        item.id === action.id ? { ...item, valueType: action.value } : item,
      );
    case 'SET_SALARY_STRUCTURE_COMPONENTS':
      return action.data;
    case 'ADD_COMPONENT':
      return [...state, { ...action.salaryComponent, id: action.id }];
    case 'REMOVE_COMPONENT':
      return state.filter((item) => item.id !== action.id);
    default:
      return state;
  }
}

type SalaryStructureProviderProps = {
  initialData?: SalaryStructureComponent[];
  children?: React.ReactNode;
};

export const SalaryStructureProvider = ({
  children,
  initialData = [],
}: SalaryStructureProviderProps) => {
  const [salaryStructureComponents, dispatch] = useReducer(salaryStructureReducer, initialData);
  const [salaryStructure, setSalaryStructureInternal] = useState<SalaryStructure>(
    {} as SalaryStructure,
  );

  const updateComponent = useCallback((id: string, value: SalaryStructureComponent) => {
    dispatch({ type: 'UPDATE_COMPONENT', id, value });
  }, []);

  const updateAmount = useCallback((id: string, value?: string) => {
    dispatch({ type: 'UPDATE_AMOUNT', id, value });
  }, []);

  const updateType = useCallback((id: string, value: SalaryStructureComponentValueType) => {
    dispatch({ type: 'UPDATE_TYPE', id, value });
  }, []);

  const setSalaryStructure = useCallback((data: SalaryStructure) => {
    setSalaryStructureInternal(data);
  }, []);

  const addComponent = useCallback((id: string, salaryComponent: SalaryStructureComponent) => {
    dispatch({ type: 'ADD_COMPONENT', id, salaryComponent });
  }, []);

  const removeComponent = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_COMPONENT', id });
  }, []);

  const setSalaryStructureComponents = useCallback((data: SalaryStructureComponent[]) => {
    dispatch({ type: 'SET_SALARY_STRUCTURE_COMPONENTS', data });
  }, []);

  const value = useMemo(
    () => ({
      salaryStructureComponents,
      salaryStructure,
      updateComponent,
      updateAmount,
      updateType,
      setSalaryStructure,
      addComponent,
      removeComponent,
      setSalaryStructureComponents,
    }),
    [
      salaryStructureComponents,
      salaryStructure,
      updateComponent,
      updateAmount,
      updateType,
      setSalaryStructure,
      addComponent,
      removeComponent,
      setSalaryStructureComponents,
    ],
  );

  return (
    <SalaryStructureContext.Provider value={value}>{children}</SalaryStructureContext.Provider>
  );
};

export const useSalaryStructure = () => {
  const context = useContext(SalaryStructureContext);
  if (!context) {
    throw new Error('useSalaryStructure must be used within SalaryStructureProvider');
  }
  return context;
};
